import { Component } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Player from '@vimeo/player'
import qs from 'qs'

import {
  dealVideoPlayed,
  dealVideoPaused,
} from 'actions/AnalyticsActions'

const Root = styled.div`
  position: relative;
  height: 100%;
`

export interface VideoData {
  offerId?: string
  videoId: string
  duration: number
  currentTime: string | number
  playSource: string
}

export interface VimeoPlayerOptions {
  controls?: boolean
  muted?: boolean
  autoplay?: boolean
  loop?: boolean
  autopause?: boolean
  /** For background videos - enables autoplay, loop, mutes, and hides controls */
  background?: boolean
  /** Required for iOS to play videos inline without going fullscreen */
  playsinline?: boolean
}

interface Props {
  vimeoVideoId: string
  offerId?: string
  onUnmount?: (component: any) => void
  dealVideoPaused?: (videoData: VideoData) => void
  dealVideoPlayed?: (videoData: VideoData) => void
  onPlay?: (component: any) => void
  onPaused?: () => void
  onBuffering?: (isBuffering: boolean) => void
  query?: VimeoPlayerOptions
  hideWhenBuffering?: boolean
}

interface State {
  playing?: boolean
  playSource?: string
  startedPlaying?: boolean
  buffering?: boolean
}

class VimeoVideo extends Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    playing: false,
    playSource: '',
    // Start with buffering false - will be set to true when bufferstart fires
    // This prevents hiding video on mobile where autoplay may be blocked
    buffering: false,
  }

  static defaultProps = {
    query: {
      controls: true,
    },
  }

  player: Player | undefined

  componentDidMount() {
    if (!this.player) {
      return
    }

    this.player.on('play', this.onPlay)
    this.player.on('pause', () => this.props.onPaused?.())
    this.player.on('bufferstart', this.onBufferStart)
    this.player.on('bufferend', this.onBufferEnd)
  }

  onBufferStart = () => {
    this.setState({ buffering: true })
    this.props.onBuffering?.(true)
  }

  onBufferEnd = () => {
    this.setState({ buffering: false })
    this.props.onBuffering?.(false)
  }

  async componentWillUnmount() {
    if (!this.player) {
      return
    }

    const { onUnmount } = this.props
    if (this.state.playing) {
      this.props.dealVideoPaused?.(await this.getVideoData())
    }
    onUnmount?.(this)
  }

  getVideoData = async(): Promise<VideoData> => {
    const { offerId, vimeoVideoId } = this.props

    return {
      offerId,
      videoId: vimeoVideoId,
      duration: await (this.player?.getDuration().then(d => d) ?? Promise.resolve(0)),
      currentTime: await (this.player?.getCurrentTime().then(ct => ct) ?? Promise.resolve(0)),
      playSource: 'carousel',
    }
  }

  play = (source: string) => {
    if (!this.player) {
      return
    }

    let playSource = source || ''

    if (!playSource && this.props.offerId) {
      playSource = 'carousel'
    }

    this.setState({
      playSource,
    }, () => {
      this.player?.play().then(async() => this.props.dealVideoPlayed?.(await this.getVideoData()))
    })
  }

  pause = () => {
    this.player?.pause().then(async() => this.props.dealVideoPaused?.(await this.getVideoData()))
    this.setState({ playing: false })
  }

  setMuted = (muted: boolean) => {
    this.player?.setVolume(muted ? 0 : 1)
  }

  onPlay = async() => {
    const { dealVideoPlayed, onPlay } = this.props
    this.setState({
      startedPlaying: true,
      playing: true,
      playSource: this.state.playSource,
    })
    dealVideoPlayed?.(await this.getVideoData())
    onPlay?.(this)
  }

  render() {
    const { vimeoVideoId, query = {}, dealVideoPaused, dealVideoPlayed, offerId, onPlay, onUnmount, onBuffering, onPaused, hideWhenBuffering, ...rest } = this.props
    const { controls, muted, autoplay, loop, autopause, background, playsinline } = query
    const { buffering } = this.state

    if (!vimeoVideoId) {
      return null
    }

    const queryParams = {
      ...(!controls && { controls: 'false' }),
      ...(!autopause && { autopause: 'false' }),
      ...(muted && { muted: '1' }),
      ...(autoplay && { autoplay: '1' }),
      ...(loop && { loop: '1' }),
      ...(background && { background: '1' }),
      ...(playsinline && { playsinline: '1' }),
    }

    const queryString = qs.stringify(queryParams)

    const iframeStyle: React.CSSProperties = {
      opacity: buffering && hideWhenBuffering ? 0 : 1,
      transition: 'opacity 0.3s ease-in-out',
    }

    return (
      <Root {...rest}>
        <iframe
          title={vimeoVideoId}
          id={`video-${vimeoVideoId}`}
          src={`https://player.vimeo.com/video/${vimeoVideoId}${queryString ? `?${queryString}` : ''}`}
          width="100%"
          height="100%"
          frameBorder="0"
          allowFullScreen
          allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture;"
          style={iframeStyle}
          ref={(v) => {
            if (!this.player && v) {
              this.player = new Player(v)
            }
          }}
        >
        </iframe>
      </Root>
    )
  }
}

const mapDispatchToProps = {
  dealVideoPlayed,
  dealVideoPaused,
}

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(VimeoVideo)
